import {Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {PerformanceMatrix} from '../../models/performanceMatrix';
import {TaxesMatrix} from '../../models/taxesMatrix';
import {TruckDataMatrix} from '../../models/truckDataMatrix';
import {UteTariff} from '../../models/uteTariff';
import Tco from '../../models/tco';
import {Observable, Subject} from 'rxjs';
import {Router} from '@angular/router';
import FormsWrapper from '../../models/formsWrapper';
import TcoList from '../../models/tcoList';

@Injectable({
  providedIn: 'root'
})
export class TruckFormService {

  // FormGroup variables
  truckFormGroup: FormGroup = new FormGroup({});
  dieselForm: FormGroup | null = null;
  electricForm: FormGroup | null = null;
  hydrogenForm: FormGroup | null = null;
  assumptionsForm: FormGroup | null = null;

  formsWrapper: FormsWrapper = {
    dieselForm: null,
    electricForm: null,
    hydrogenForm: null,
    assumptionsForm: null
  };

  // Form selection booleans
  dieselFormSelected = false;
  electricFormSelected = false;
  hydrogenFormSelected = false;

  // TCO variables
  dieselTCO: Tco | null = null;
  electricTCO: Tco | null = null;
  hydrogenTCO: Tco | null = null;

  constructor(private uteTariff: UteTariff) {
  }

  // Diesel theoretical performance
  private performanceMatrix: PerformanceMatrix = {
    payload: [25, 5.5, 2.5, 0],
    urban: [50.8362921918007, 26.0952441462196, 17.6, 0],
    regional: [41.3168079096045, 22.3045, 15.9, 0],
    longDistance: [37.1851271186441, 20.5943213636364, 14.9, 0]
  };

  // Hydrogen theoretical performance
  private hPerformanceMatrix: PerformanceMatrix = {
    payload: [20, 10, 0],
    urban: [5, 2.53, 0],
    regional: [5, 2.53, 0],
    longDistance: [4.1, 1.95, 0]
  };

  // Average speed in km/h
  private averageSpeedMatrix: TruckDataMatrix = {
    urban: 20,
    regional: 40,
    longDistance: 80
  };

  private maintenanceCostMatrix: TruckDataMatrix = {
    urban: 0.00115,
    regional: 0.00172,
    longDistance: 0.0025
  };

  // pos0 -> diesel, pos1 -> electric, pos2 -> hydrogen
  private taxesMatrix: TaxesMatrix = {
    tga: [0.15, 0, 0],
    consularFee: [0.05, 0.05, 0.05]
  };

  // Cell power in kW
  private cellPowerMatrix: TruckDataMatrix = {
    urban: 30,
    regional: 100,
    longDistance: 230
  };

  private ratioDiscountMatrix = [[0.5, 0.25], [0.3778, 0.3]];

  private dieselTcoSource = new Subject<Tco>();
  private _dieselTcoSourceMessage = this.dieselTcoSource.asObservable();

  private electricTcoSource = new Subject<Tco>();
  private _electricTcoSourceMessage = this.electricTcoSource.asObservable();

  private hydrogenTcoSource = new Subject<Tco>();
  private _hydrogenTcoSourceMessage = this.hydrogenTcoSource.asObservable();

  static getNumberValue(truckForm: FormGroup | null, key: string): number {
    return truckForm ? parseFloat(truckForm.get(key)?.value) : -1;
  }


  get dieselTcoSourceMessage(): Observable<Tco> {
    return this._dieselTcoSourceMessage;
  }

  get electricTcoSourceMessage(): Observable<Tco> {
    return this._electricTcoSourceMessage;
  }

  get hydrogenTcoSourceMessage(): Observable<Tco> {
    return this._hydrogenTcoSourceMessage;
  }

  saveDieselForm(truckFormGroup: FormGroup): void {
    this.dieselForm = truckFormGroup;
  }

  saveElectricForm(truckFormGroup: FormGroup): void {
    this.electricForm = truckFormGroup;
  }

  saveHydrogenForm(truckFormGroup: FormGroup): void {
    this.hydrogenForm = truckFormGroup;
  }

  setFormBooleans(dieselFormBoolean: boolean, electricFormBoolean: boolean, hydrogenFormBoolean: boolean): void {
    this.dieselFormSelected = dieselFormBoolean;
    this.electricFormSelected = electricFormBoolean;
    this.hydrogenFormSelected = hydrogenFormBoolean;
  }

  saveAssumptionsForm(assumptionsForm: FormGroup): void {
    this.assumptionsForm = assumptionsForm;
  }

  loadTcos(): TcoList {
    return {dieselTCO: this.dieselTCO, electricTCO: this.electricTCO, hydrogenTCO: this.hydrogenTCO};
  }

  calculateTCO(): void {
    const truckForm = this.toTruckForm();

    if (!truckForm || !truckForm.dieselForm) {
      return;
    }

    this.formsWrapper = truckForm;

    const dieselTCO = this.calculateDieselTCO(truckForm);
    this.dieselTCO = dieselTCO;


    if (this.electricFormSelected && truckForm.electricForm) {
      const electricTCO = this.calculateElectricTCO(truckForm, this.dieselTCO.annualMaintenanceCosts, this.dieselTCO.maintenance);
      this.electricTCO = electricTCO;
    }

    if (this.hydrogenFormSelected && truckForm.hydrogenForm) {
      const hydrogenTCO = this.calculateHydrogenTCO(truckForm, this.dieselTCO.annualMaintenanceCosts, this.dieselTCO.maintenance);
      this.hydrogenTCO = hydrogenTCO;
    }
  }

  private toTruckForm(): FormsWrapper | null {

    if (!this.assumptionsForm || !this.dieselForm) {
      return null;
    }

    const formsWrapper: FormsWrapper = {
      dieselForm: null,
      electricForm: null,
      hydrogenForm: null,
      assumptionsForm: null
    };

    const averageSpeed = this.getFormValue(this.dieselForm, 'averageSpeed');
    const performance = this.getFormValue(this.dieselForm, 'performance');
    if (this.dieselFormSelected) {
      const maintenanceCost = this.getFormValue(this.dieselForm, 'maintenanceCost');
      const insuranceCost = this.getFormValue(this.dieselForm, 'insuranceCost',
        [this.dieselForm.get('truckPrice')?.value, 0.017]);
      formsWrapper.dieselForm = {...this.dieselForm.value, performance, averageSpeed, maintenanceCost, insuranceCost};
    }

    if (this.electricFormSelected && this.electricForm) {
      const battery = this.getFormValue(this.electricForm, 'battery', [averageSpeed ?? 'regional', performance ?? 4.5]);
      const eTruckPrice = this.getFormValue(this.electricForm, 'eTruckPrice', [battery]);
      const eInsuranceCost = this.getFormValue(this.electricForm, 'eInsuranceCost', [eTruckPrice, 0.01]);
      // Electric truck COMAP discount
      let discounts = 0;
      if (this.electricForm.get('comapDiscountSwitch')?.value) {
        const cif = this.getCIF(this.dieselForm.get('truckPrice')?.value ?? 90000, battery);
        const originalValue = this.electricForm?.get('eTruckPrice')?.value ?? cif;
        discounts = this.getCOMAPDiscount(originalValue);
      }
      formsWrapper.electricForm = {...this.electricForm.value, battery, eInsuranceCost, eTruckPrice, discounts};
    }

    if (this.hydrogenFormSelected && this.hydrogenForm) {
      const tankSize = this.getFormValue(this.hydrogenForm, 'tankSize');
      const cellPower = this.getFormValue(this.hydrogenForm, 'cellPower');
      const hTruckPrice = this.getFormValue(this.hydrogenForm, 'hTruckPrice', [tankSize, cellPower]);
      const hInsuranceCost = this.getFormValue(this.hydrogenForm, 'hInsuranceCost', [hTruckPrice, 0.012]);
      let discounts = 0;
      // Hydrogen Truck COMAP discount
      if (this.hydrogenForm?.get('hComapDiscountSwitch')?.value) {
        discounts = this.getCOMAPDiscount(hTruckPrice);
      }
      formsWrapper.hydrogenForm = {...this.hydrogenForm.value, tankSize, cellPower, hInsuranceCost, hTruckPrice, discounts};
    }

    formsWrapper.assumptionsForm = {...this.assumptionsForm.value};
    Object.keys(formsWrapper.assumptionsForm).forEach(key => {
      if (['inflation', 'discountRate', 'hUpgradeRate', 'gasoil', 'electricity'].includes(key)) {
        formsWrapper.assumptionsForm[key] *= 0.01;
      }
    });

    return formsWrapper;

    // return {
    //   // Truck
    //   grossWeight: TruckFormService.getNumberValue(this.dieselForm, 'grossWeight'),
    //   tare: TruckFormService.getNumberValue(this.dieselForm, 'tare'),
    //   vehicleChange: TruckFormService.getNumberValue(this.dieselForm, 'vehicleChange'),
    //   // Trajectory
    //   distanceTraveled: TruckFormService.getNumberValue(this.dieselForm, 'distanceTraveled'),
    //   performance,
    //   averageSpeed,
    //   transport: this.dieselForm.get('transport')?.value,
    //   operatingDays: TruckFormService.getNumberValue(this.dieselForm, 'operatingDays'),
    //   // Gas Cost
    //   gasCost: TruckFormService.getNumberValue(this.dieselForm, 'gasCost'),
    //   maintenanceCost: maintenanceCostVal,
    //   insuranceCost: insuranceCostVal,
    //   // truckPrice: TruckFormService.getNumberValue(this.truckFormGroup, 'truckPrice'),
    //   truckPrice: this.dieselForm?.get('truckPrice')?.value,
    //   lifespan: TruckFormService.getNumberValue(this.assumptionsForm, 'dLifespan'),
    //   // Truck
    //   eGrossWeight: TruckFormService.getNumberValue(this.electricForm, 'eGrossWeight'),
    //   eTare: TruckFormService.getNumberValue(this.electricForm, 'eTare'),
    //   battery: batteryVal,
    //   // Electricity
    //   uteFare: this.truckFormGroup.get('uteFare')?.value,
    //   hiredPower: TruckFormService.getNumberValue(this.electricForm, 'hiredPower'),
    //   chargingSchedule: this.electricForm?.get('chargingSchedule')?.value,
    //   chargingTime: TruckFormService.getNumberValue(this.electricForm, 'chargingTime'),
    //   // Economy
    //   eInsuranceCost: eInsuranceCostVal,
    //   eTruckPrice: eTruckPriceVal,
    //   eLifespan: TruckFormService.getNumberValue(this.assumptionsForm, 'eLifespan'),
    //   // Truck
    //   hGrossWeight: TruckFormService.getNumberValue(this.hydrogenForm, 'hGrossWeight'),
    //   hTare: TruckFormService.getNumberValue(this.hydrogenForm, 'hTare'),
    //   tankSize: tankSizeVal,
    //   cellPower: cellPowerVal,
    //   // Economy
    //   hydrogenCost: TruckFormService.getNumberValue(this.hydrogenForm, 'hydrogenCost'),
    //   hInsuranceCost: hInsuranceCostVal,
    //   hTruckPrice: hTruckPriceVal,
    //   hLifespan: TruckFormService.getNumberValue(this.assumptionsForm, 'hLifespan'),
    //   hInfrastructureCost: TruckFormService.getNumberValue(this.assumptionsForm, 'hInfrastructureCost'),
    //   hUpgradeRate: 0.01 * TruckFormService.getNumberValue(this.assumptionsForm, 'hUpgradeRate'),
    //   // Additional Data
    //   pesoDollarConv: TruckFormService.getNumberValue(this.hydrogenForm, 'pesoDollarConv'),
    //   inflation: 0.01 * this.assumptionsForm.get('inflation')?.value,
    //   discountRate: 0.01 * this.assumptionsForm.get('discountRate')?.value
    // };
  }

  private getFormValue(truckFormGroup: FormGroup | null, key: string, necessaryValues?: number[]): number {
    return truckFormGroup ? (truckFormGroup.get(key)?.value ?
      truckFormGroup.get(key)?.value : this.getDefaultValue(key, necessaryValues)) : -1;
  }

  private getDefaultValue(key: string, necessaryValues?: number[]): number {
    if (!this.dieselForm) {
      return -1;
    }

    if (key === 'performance') {
      return 100 / this.calculatePerformance(
        this.dieselForm.get('grossWeight')?.value,
        this.dieselForm.get('tare')?.value,
        this.dieselForm.get('transport')?.value,
        this.performanceMatrix);
    }

    if (key === 'averageSpeed') {
      return this.calculateAverageSpeed(this.dieselForm.get('transport')?.value);
    }

    if (key === 'maintenanceCost') {
      return this.calculateMaintenanceCost(
        this.dieselForm.get('transport')?.value,
        this.dieselForm.get('dailyDistance')?.value,
        this.dieselForm.get('operatingDays')?.value,
        this.dieselForm.get('truckPrice')?.value
      );
    }

    if (key === 'insuranceCost' || key === 'eInsuranceCost' || key === 'hInsuranceCost') {
      return this.calculateInsuranceCost(
        necessaryValues ? necessaryValues[0] : 0,
        necessaryValues ? necessaryValues[1] : 0,
        this.assumptionsForm?.get('pesoDollarConv')?.value ?? 44);
    }

    if (key === 'battery') {
      return this.calculateBattery(
        this.dieselForm.get('dailyDistance')?.value,
        necessaryValues ? necessaryValues[0] : 0,
        necessaryValues ? necessaryValues[1] : 0
      );
    }

    if (key === 'eTruckPrice') {
      return this.calculateETruckPrice(
        this.dieselForm.get('truckPrice')?.value,
        necessaryValues ? necessaryValues[0] : 0
      );
    }

    if (key === 'tankSize') {
      return this.calculateHPerformance(
        this.hydrogenForm?.get('hGrossWeight')?.value,
        this.hydrogenForm?.get('hTare')?.value,
        this.dieselForm.get('transport')?.value,
        this.dieselForm.get('dailyDistance')?.value
      );
    }

    if (key === 'cellPower') {
      return this.calculateCellPower(this.dieselForm.get('transport')?.value);
    }

    if (key === 'hTruckPrice') {
      return this.calculateHTruckPrice(
        this.dieselForm.get('truckPrice')?.value,
        necessaryValues ? necessaryValues[0] : 0,
        necessaryValues ? necessaryValues[1] : 0
      );
    }

    return 0;
  }

  // COMAP discount related methods
  private getCOMAPDiscount(originalValue: number): number {
    const alpha = this.getAlpha(1);
    const discount = this.getDiscount(1, alpha);

    return originalValue * discount;
  }

  private getInvestment(investment: boolean, majorInvestment: number, originalValue: number): number {
    return investment ? originalValue : majorInvestment;
  }

  private getAlpha(ratio: number): number {
    if (ratio > this.ratioDiscountMatrix[0][0]) {
      return 0;
    }

    if (ratio < this.ratioDiscountMatrix[0][1]) {
      return 1;
    }

    return (this.ratioDiscountMatrix[0][0] - ratio) / (this.ratioDiscountMatrix[0][0] - this.ratioDiscountMatrix[0][1]);
  }

  private getDiscount(ratio: number, alpha: number): number {
    return ratio < this.ratioDiscountMatrix[0][1] ?
      0 : alpha * this.ratioDiscountMatrix[1][1] + (1 - alpha) * this.ratioDiscountMatrix[1][0];
  }

  private calculatePerformance(grossWeight: number, tare: number, transport: string, performanceMatrix: PerformanceMatrix): number {

    const payLoad = grossWeight - tare;

    let index = performanceMatrix.payload.length - 2;

    for (let i = 0; i < performanceMatrix.payload.length - 2; i++) {
      if (payLoad < performanceMatrix.payload[i] && payLoad > performanceMatrix.payload[i + 1]) {
        index = i;
        break;
      }
    }

    // const index = payLoad < performanceMatrix.payload[0] && payLoad > performanceMatrix.payload[1] ? 0 :
    //   payLoad < performanceMatrix.payload[1] && payLoad > performanceMatrix.payload[2] ? 1 : 2;

    const alpha = (payLoad - performanceMatrix.payload[index + 1]) /
      (performanceMatrix.payload[index] - performanceMatrix.payload[index + 1]);

    const theoreticalConsumption = alpha *
      (performanceMatrix[transport as keyof PerformanceMatrix][index] -
        performanceMatrix[transport as keyof PerformanceMatrix][index + 1])
      + performanceMatrix[transport as keyof PerformanceMatrix][index + 1];

    return theoreticalConsumption;
  }

  private calculateAverageSpeed(transport: string): number {
    return this.averageSpeedMatrix[transport as keyof TruckDataMatrix];
  }

  private calculateMaintenanceCost(transport: string, distanceTraveled: number, operatingDays: number, truckPrice: number): number {
    return (distanceTraveled * operatingDays / 1000) * this.maintenanceCostMatrix[transport as keyof TruckDataMatrix] * truckPrice;
  }

  private calculateInsuranceCost(truckPrice: number, percentage: number, pesoDollarConv: number): number {
    return truckPrice * percentage * pesoDollarConv;
  }

  private calculateBattery(distanceTraveled: number, averageSpeed: number, performance: number): number {
    const maxConsumption = this.calculateAverageConsumption(averageSpeed, performance) * 1.2;
    return maxConsumption * distanceTraveled * 1.5;
  }

  private calculateAverageConsumption(averageSpeed: number, performance: number): number {
    const energyPerDistance = 10.6 / performance;
    const efficiencyRatio = (0.0003 * Math.pow(averageSpeed, 2)) - (0.0692 * averageSpeed) + 6.3495;
    return energyPerDistance / efficiencyRatio;
  }

  private calculateETruckPrice(truckPrice: number, battery: number): number {
    return this.getCIF(truckPrice, battery) * (1 + this.taxesMatrix.tga[1] + this.taxesMatrix.consularFee[1]);
  }

  private getCIF(truckPrice: number, battery: number): number {
    return (0.988 * truckPrice) + (150 * battery);
  }

  private getTheoreticalConsumption(hGrossWeight: number, hTare: number, transport: string): number {
    return this.calculatePerformance(hGrossWeight, hTare, transport, this.hPerformanceMatrix);
  }

  private calculateHPerformance(hGrossWeight: number, hTare: number, transport: string,
                                distanceTraveled: number): number {
    const theoreticalConsumption = this.getTheoreticalConsumption(hGrossWeight, hTare, transport);
    const maxConsumption = 1.2 * theoreticalConsumption * 0.03;   // maxConsumption = 1.2 * averageConsumption
    return 1.5 * maxConsumption * distanceTraveled;
  }

  private calculateCellPower(transport: string): number {
    return this.cellPowerMatrix[transport as keyof TruckDataMatrix];
  }

  private calculateHTruckPrice(truckPrice: number, tankSize: number, cellPower: number): number {
    return (1.4 * truckPrice + 35 * tankSize + 1275 * cellPower) * (1 + this.taxesMatrix.tga[2] + this.taxesMatrix.consularFee[2]);
  }

  private getAnnualCosts(callbackFn: (index: number) => number, vehicleChange: number): number[] {
    const anualCosts: number[] = [];

    for (let i = 1; i <= vehicleChange; i++) {
      anualCosts.push(callbackFn(i));
    }

    return anualCosts;
  }

  private getTotalDieselCost(fuelCost: number, vehicleChange: number, distanceTraveledPerYear: number, performance: number,
                             discountRate: number, inflation: number, pesoDollarConv: number): number {
    const commonValue = distanceTraveledPerYear * fuelCost / (performance * pesoDollarConv);
    return discountRate === inflation ? commonValue * vehicleChange :
      commonValue * (1 - Math.pow((1 + inflation) / (1 + discountRate), vehicleChange)) / (discountRate - inflation);
  }

  private getDieselAnnualCosts(fuelCost: number, vehicleChange: number, distanceTraveledPerYear: number, performance: number,
                               discountRate: number, inflation: number, pesoDollarConv: number): number[] {
    return this.getAnnualCosts(
      year => distanceTraveledPerYear * fuelCost *
        Math.pow(1 + inflation, year - 1) / (performance * pesoDollarConv * Math.pow(1 + discountRate, year)), vehicleChange);
  }

  private getTotalFuelCost(fuelCost: number, vehicleChange: number, distanceTraveledPerYer: number,
                           averageConsumption: number, discountRate: number, inflation: number): number {
    const commonValue = fuelCost * averageConsumption * distanceTraveledPerYer;
    return discountRate === inflation ? commonValue * vehicleChange / (1 + discountRate) :
      commonValue * (1 - Math.pow((1 + inflation) / (1 + discountRate), vehicleChange)) / (discountRate - inflation);

  }

  private getFuelAnnualCosts(fuelCost: number, vehicleChange: number, distanceTraveledPerYer: number,
                             averageConsumption: number, discountRate: number, inflation: number): number[] {
    return this.getAnnualCosts(
      year => fuelCost * averageConsumption *
        distanceTraveledPerYer * Math.pow(1 + inflation, year - 1) / Math.pow(1 + discountRate, year), vehicleChange);
  }

  private getTotalMaintenanceCost(maintenanceCost: number, vehicleChange: number, inflation: number, discountRate: number): number {
    return discountRate === inflation ? (maintenanceCost * vehicleChange) / (1 + discountRate) :
      (maintenanceCost * (1 - Math.pow((1 + inflation) / (1 + discountRate), vehicleChange)) / (discountRate - inflation));
  }

  private getMaintenanceAnnualCosts(maintenanceCost: number, vehicleChange: number, inflation: number, discountRate: number): number[] {
    return this.getAnnualCosts(
      year => maintenanceCost * Math.pow(1 + inflation, year - 1) / Math.pow(1 + discountRate, year), vehicleChange);
  }

  private getResidualValue(truckPrice: number, vehicleChange: number, lifespan: number, discountRate: number): number {
    return truckPrice * (1 - vehicleChange / lifespan) / Math.pow((1 + discountRate), vehicleChange);
  }

  private getTotalInsuranceCost(insuranceCost: number, inflation: number, discountRate: number, vehicleChange: number,
                                pesoDollarConv: number): number {

    return discountRate === 0 ? insuranceCost * vehicleChange / pesoDollarConv :
      insuranceCost * (1 - Math.pow((1 + inflation) / (1 + discountRate), vehicleChange)) / ((discountRate - inflation) * pesoDollarConv);
  }

  private getInsuranceAnnualCosts(insuranceCost: number, inflation: number, discountRate: number, vehicleChange: number,
                                  pesoDollarConv: number): number[] {
    return this.getAnnualCosts(
      index => insuranceCost * Math.pow(1 + inflation, index - 1) / (Math.pow(1 + discountRate, index) * pesoDollarConv), vehicleChange);
  }

  private getPatent(truckPrice: number, discountRate: number, vehicleChange: number): number {
    return discountRate === 0 ? 0.013 * truckPrice * vehicleChange :
      (0.013 * truckPrice * (1 - 1 / Math.pow(1 + discountRate, vehicleChange))) / discountRate;
  }

  private getPatentAnnualCosts(truckPrice: number, discountRate: number, vehicleChange: number): number[] {
    return this.getAnnualCosts(index => 0.013 * truckPrice / Math.pow(1 + discountRate, index), vehicleChange);
  }

  private getHInfrastructure(hInfrastructureCost: number, distanceTraveledPerYear: number, vehicleChange: number,
                             averageConsumption: number, discountRate: number): number {
    const commonValue = hInfrastructureCost * averageConsumption * distanceTraveledPerYear;
    return discountRate === 0 ? commonValue * discountRate :
      commonValue * (1 - 1 / Math.pow(1 + discountRate, vehicleChange)) / discountRate;
  }

  private calculateDieselTCO({dieselForm, assumptionsForm, hydrogenForm}: FormsWrapper): Tco {
    const distanceTraveledPerYear = dieselForm.dailyDistance * dieselForm.operatingDays;

    const totalGasCost = this.getTotalDieselCost(dieselForm.gasCost, dieselForm.vehicleChange, distanceTraveledPerYear,
      dieselForm.performance, assumptionsForm.discountRate, assumptionsForm.gasoil, assumptionsForm.pesoDollarConv);
    const annualGasCosts = this.getDieselAnnualCosts(dieselForm.gasCost, dieselForm.vehicleChange, distanceTraveledPerYear,
      dieselForm.performance, assumptionsForm.discountRate, assumptionsForm.gasoil, assumptionsForm.pesoDollarConv);

    const totalMaintenanceCost = this.getTotalMaintenanceCost(dieselForm.maintenanceCost, dieselForm.vehicleChange,
      assumptionsForm.inflation, assumptionsForm.discountRate);
    const annualMaintenanceCosts = this.getMaintenanceAnnualCosts(dieselForm.maintenanceCost, dieselForm.vehicleChange,
      assumptionsForm.inflation, assumptionsForm.discountRate);

    const residualValue = this.getResidualValue(dieselForm.truckPrice, dieselForm.vehicleChange,
      assumptionsForm.dLifespan, assumptionsForm.discountRate);


    const totalInsurance = this.getTotalInsuranceCost(dieselForm.insuranceCost, assumptionsForm.inflation, assumptionsForm.discountRate,
      dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);
    const annualInsuranceCosts = this.getInsuranceAnnualCosts(dieselForm.insuranceCost, assumptionsForm.inflation,
      assumptionsForm.discountRate, dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);

    const patent = this.getPatent(dieselForm.truckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);
    const annualPatentCosts = this.getPatentAnnualCosts(dieselForm.truckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);

    return new Tco(dieselForm.truckPrice, 0, totalGasCost, totalMaintenanceCost, residualValue, 0, totalInsurance,
      patent, 0, annualGasCosts, annualMaintenanceCosts, annualInsuranceCosts, annualPatentCosts);
  }

  private calculateElectricTCO({dieselForm, electricForm, hydrogenForm, assumptionsForm}: FormsWrapper, annualDMaintenanceCosts: number[],
                               dieselMaintenanceCost: number): Tco {
    const distanceTraveledPerYear = dieselForm.dailyDistance * dieselForm.operatingDays;
    const efficiencyRatio = 0.0003 * Math.pow(dieselForm.averageSpeed, 2) - 0.0692 * dieselForm.averageSpeed + 6.3495;
    const averageConsumption = this.calculateAverageConsumption(dieselForm.averageSpeed, dieselForm.performance);
    const annualCycles = averageConsumption * distanceTraveledPerYear / electricForm.battery;
    const batteryLifespan = Math.round(assumptionsForm.batteryNumberOfCycles / annualCycles);


    const totalFuelCost = this.getTotalFuelCost(
      this.uteTariff.getEnergyCost(electricForm.uteFare, electricForm.chargingSchedule) / (assumptionsForm.pesoDollarConv),
      dieselForm.vehicleChange,
      distanceTraveledPerYear,
      10.6 / (dieselForm.performance * efficiencyRatio),
      assumptionsForm.discountRate,
      assumptionsForm.electricity
    );
    const annualFuelCosts = this.getFuelAnnualCosts(
      this.uteTariff.getEnergyCost(electricForm.uteFare, electricForm.chargingSchedule) / (assumptionsForm.pesoDollarConv),
      dieselForm.vehicleChange,
      distanceTraveledPerYear,
      10.6 / (dieselForm.performance * efficiencyRatio),
      assumptionsForm.discountRate,
      assumptionsForm.electricity
    );

    const maintenanceCost = 0.605 * dieselMaintenanceCost;
    const annualMaintenanceCosts = annualDMaintenanceCosts.map(value => 0.605 * value);

    const batteryResidualValue = this.getResidualValue(
      electricForm.battery * 150,
      dieselForm.vehicleChange,
      ((batteryLifespan < assumptionsForm.batteryTimeWarranty) &&
      (batteryLifespan * distanceTraveledPerYear < assumptionsForm.batteryDistanceWarranty) ? 2 : 1) * batteryLifespan,
      assumptionsForm.discountRate
    );


    const residualValue = this.getResidualValue(
      electricForm.eTruckPrice - electricForm.battery * 150,
      dieselForm.vehicleChange,
      assumptionsForm.eLifespan,
      assumptionsForm.discountRate);

    // const extraBattery = electricForm.batteryReplacement ? 150 * electricForm.battery : 0;
    const totalInsurance = this.getTotalInsuranceCost(electricForm.eInsuranceCost, assumptionsForm.inflation,
      assumptionsForm.discountRate, dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);
    const annualInsuranceCosts = this.getInsuranceAnnualCosts(electricForm.eInsuranceCost, assumptionsForm.inflation,
      assumptionsForm.discountRate, dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);


    const patent = this.getPatent(electricForm.eTruckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);
    const annualPatentCosts = this.getPatentAnnualCosts(electricForm.eTruckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);

    const infrastructureCost = 20 * assumptionsForm.chargingStationCostPerKW * Math.ceil(
      electricForm.battery / (20 * electricForm.chargingTime));

    return new Tco(electricForm.eTruckPrice, electricForm.discounts, totalFuelCost, maintenanceCost, residualValue,
      batteryResidualValue, totalInsurance, patent, infrastructureCost, annualFuelCosts, annualMaintenanceCosts, annualInsuranceCosts,
      annualPatentCosts);
  }

  private calculateHydrogenTCO({dieselForm, hydrogenForm, assumptionsForm}: FormsWrapper, annualDMaintenanceCosts: number[],
                               dieselMaintenanceCost: number): Tco {

    const averageConsumption = 0.03 * this.getTheoreticalConsumption(hydrogenForm.hGrossWeight, hydrogenForm.hTare,
      dieselForm.transport);
    const distanceTraveledPerYear = dieselForm.dailyDistance * dieselForm.operatingDays;

    const totalFuelCost = this.getTotalFuelCost(hydrogenForm.hydrogenCost, dieselForm.vehicleChange, distanceTraveledPerYear,
      averageConsumption, assumptionsForm.discountRate, -assumptionsForm.hUpgradeRate);
    const annualFuelCosts = this.getFuelAnnualCosts(hydrogenForm.hydrogenCost, dieselForm.vehicleChange, distanceTraveledPerYear,
      averageConsumption, assumptionsForm.discountRate, -assumptionsForm.hUpgradeRate);

    const maintenanceCost = 1.091 * dieselMaintenanceCost;
    const annualMaintenanceCosts = annualDMaintenanceCosts.map(value => 1.091 * value);

    const residualValue = this.getResidualValue(hydrogenForm.hTruckPrice, dieselForm.vehicleChange, assumptionsForm.hLifespan,
      assumptionsForm.discountRate);

    const totalInsurance = this.getTotalInsuranceCost(hydrogenForm.hInsuranceCost, assumptionsForm.inflation, assumptionsForm.discountRate,
      dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);
    const annualInsuranceCosts = this.getInsuranceAnnualCosts(hydrogenForm.hInsuranceCost, assumptionsForm.inflation,
      assumptionsForm.discountRate, dieselForm.vehicleChange, assumptionsForm.pesoDollarConv);

    const patent = this.getPatent(hydrogenForm.hTruckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);
    const annualPatentCosts = this.getPatentAnnualCosts(hydrogenForm.truckPrice, assumptionsForm.discountRate, dieselForm.vehicleChange);

    const infrastructure = this.getHInfrastructure(assumptionsForm.hInfrastructureCost, distanceTraveledPerYear, dieselForm.vehicleChange,
      averageConsumption, assumptionsForm.discountRate);

    return new Tco(hydrogenForm.hTruckPrice, hydrogenForm.discounts, totalFuelCost, maintenanceCost,
      residualValue, 0, totalInsurance, patent, infrastructure, annualFuelCosts, annualMaintenanceCosts,
      annualInsuranceCosts, annualPatentCosts);
  }
}
